import axios from "axios";
import React, { useEffect, useState } from "react";
import getConfig from "next/config";

import { User, UserLocation } from "@themuse/design-system/lib/main";
import { formatLog } from "utils/logging";
import mockUser from "../mocks/mockuser.json";

export interface AppUser extends User {
    isInitialized?: boolean;
}

interface UserProviderProps {
    user?: AppUser;
    userLocation?: UserLocation;
}

const defaultUserState: AppUser = {
    isInitialized: false,
    logged_in: false,
    view_bookings: false
};

const UserContext = React.createContext<AppUser>(defaultUserState);

const UserProvider: React.FC<React.PropsWithChildren<UserProviderProps>> = (props) => {
    const { children, user, userLocation } = props;
    const initialState = user ? {
        ...user,
        location: userLocation
    } : {
        ...defaultUserState,
        location: userLocation
    };
    const [userState, setUserState] = useState(initialState);
    useEffect(() => {
        let isMounted = true;
        const { publicRuntimeConfig: { apiRequestTimeoutMs, museApiHost, appName } } = getConfig();
        const fetchUser = async () => {
            if (!["www.themuse.com", "https://www.staging-themuse.com/"].includes(window.location.host)) {
                setUserState(mockUser);
            } else {
                try {
                    const response = await axios.get(`${museApiHost}/api/user`, { timeout: apiRequestTimeoutMs });
                    if (response && isMounted) {
                        setUserState({
                            ...response.data as AppUser,
                            location: userState.location,
                            isInitialized: true
                        });
                    }
                } catch (error) {
                    const msg = `${appName} was unable to fetch current user from ${museApiHost}/api/user.`;
                    console.error(msg, JSON.stringify(formatLog({ error })));
                    setUserState({ ...userState, isInitialized: true });
                }
            }
        };

        fetchUser();
        return () => {
            isMounted = false;
        };
    }, []);

    return <UserContext.Provider value={{ ...userState, location: userLocation }}>{children}</UserContext.Provider>;
};

const useUserState = () => {
    const context = React.useContext(UserContext);
    if (context === undefined) {
        throw new Error("useUserState must be used within the SearchProvider");
    }
    return context;
};

export { UserProvider, useUserState };
